import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faDownload } from '@fortawesome/free-solid-svg-icons';
import Toast from '../../Toast/Toast';
import AWS from 'aws-sdk';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../../../../config/config';
import './MultiFileUploadS3.css';
import { useKeysStore } from '../../../../../../../core/store/useKeysStore';



const MultiFileUploadS3 = (props) => {
  const { keys } = useKeysStore();
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [tableWidth, setTableWidth] = useState(0);
  const uploadRef = useRef(null);

  const limitSize = props.limitFileSize ? props.limitFileSize : 10;

  AWS.config.update({
    accessKeyId: keys?.bucketS3?.accessKeyId,
    secretAccessKey: keys?.bucketS3?.secretAccessKey,
    region: keys?.bucketS3?.region,
  });

  const handleChange = async (event) => {
    if (props.disabled) {
      Toast.error(`Upload de arquivo desabilitado.`);
      return;
    }

    const uploadedFiles = Array.from(event.target.files);

    if (uploadedFiles.length + files.length > props.uploadFileLimit) {
      Toast.error(`Você não pode inserir mais do que ${props.uploadFileLimit} arquivos.`);
      return;
    }

    const tooLargeFiles = uploadedFiles.filter((file) => file.size / (1024 * 1024) > limitSize);

    if (tooLargeFiles.length > 0) {
      Toast.error(
        `Os seguintes arquivos não podem ser maiores que ${limitSize} MB(s): ${tooLargeFiles
          .map((file) => file.name)
          .join(', ')}`
      );
      return;
    }

    const currentDate = moment().format('YYYY/MM/DD');
    const s3 = new AWS.S3();

    const filesData = await Promise.all(
      uploadedFiles.map(async (file) => {
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        const uniqueId = uuidv4();

        let fileUrl = `${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;

        if (keys?.bucketS3 && keys?.bucketS3.path_default && keys.bucketS3.path_default != '') {
          fileUrl = `${keys.bucketS3.path_default}/${props.path}/uploads/${currentDate}/${uniqueId}.${fileExtension}`;
        } else {
          Toast.error('Erro ao fazer o upload do arquivo. É necessário informar o path_default nas configurações.');
          return;
        }

        const params = {
          Bucket: keys?.bucketS3?.bucket,
          Key: fileUrl,
          Body: file,
        };

        try {
          beforeUpload();
          const s3Upload = s3.upload(params);

          s3Upload.on('httpUploadProgress', (progress) => {
            const uploadProgress = Math.round((progress.loaded / progress.total) * 100);
            setUploadProgress(uploadProgress);
          });

          const data = await s3Upload.promise();
          setUploadProgress(null);
          return {
            filename: fileName,
            files3: data.Location,
          };
        } catch (error) {
          Toast.error('Erro ao fazer o upload do arquivo.');
          return null;
        }
      })
    );

    const uploadedFilesData = filesData.filter(Boolean);
    setFiles((prevFiles) => [...prevFiles, ...uploadedFilesData]);
    props.onUpload(props.name, [...files, ...uploadedFilesData]);
  };

  const beforeUpload = () => {
    props.beforeUpload();
  };

  const generateDeleteUrl = (fileUrl) => {
    const s3 = new AWS.S3();
    const bucketName = keys?.bucketS3?.bucket;
    const key = fileUrl.substring(fileUrl.indexOf(bucketName) + bucketName.length + 1);

    const params = {
      Bucket: bucketName,
      Key: key,
    };

    const url = s3.getSignedUrl('deleteObject', params);
    return url;
  };


  const deleteFile = async (fileUrl) => {
    const fileToDelete = files.find((file) => file.files3 === fileUrl);
    const deleteUrl = generateDeleteUrl(fileToDelete.files3);
    try {
      await fetch(deleteUrl, {
        method: 'DELETE',
      });
      setFiles((prevFiles) => prevFiles.filter((file) => file.files3 !== fileUrl));
      if (typeof props.onRemove === 'function') {
        props.onRemove(props.name, fileToDelete);
      }
    } catch (error) {
      Toast.error('Erro ao excluir o arquivo.');
    }
  };

  const clickRemove = (fileUrl) => {
    deleteFile(fileUrl);
  };

  const generateDownloadUrl = (fileUrl) => {
    const s3 = new AWS.S3();
    const bucketName = keys?.bucketS3?.bucket;
    const key = fileUrl.substring(fileUrl.indexOf(bucketName) + bucketName.length + 1);

    const params = {
      Bucket: bucketName,
      Key: key,
    };

    const url = s3.getSignedUrl('getObject', params);
    return url;
  };


  useEffect(() => {
    const updateTableWidth = () => {
      const tableElement = document.querySelector('.file-table');
      if (tableElement) {
        setTableWidth(tableElement.clientWidth);
      }
    };

    window.addEventListener('resize', updateTableWidth);
    updateTableWidth();

    return () => {
      window.removeEventListener('resize', updateTableWidth);
    };
  }, []);


  useEffect(() => {
    if (props.file && props.file.length > 0) {
      setFiles(props.file);
    }
  }, [props.file]);


  const renderTable = () => {
    if (files.length === 0) {
      return null;
    }

    return (
      <table className="file-table">
        <thead>
          <tr>
            <th>Arquivo</th>
            <th>Download</th>
            {!props.disabled && (
              <th>Excluir</th>
            )}
          </tr>
        </thead>
        <tbody>
          {files.map((fileData) => (
            <tr key={fileData.files3}>
              <td>{fileData.filename}</td>
              <td width={'20px'} style={{ textAlign: 'center' }}>
                {fileData.files3 ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(generateDownloadUrl(fileData.files3), "_blank");
                    }}
                    className="download-button"
                  >
                    <FontAwesomeIcon icon={faDownload} />
                  </button>
                ) : (
                  <span>Arquivo não enviado!</span>
                )}
              </td>
              {!props.disabled && (
                <td width={'20px'} style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => clickRemove(fileData.files3)}
                    className="delete-button"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  useEffect(() => {
    return () => {
      if (uploadRef.current) {
        uploadRef.current.value = '';
      }
    };
  }, []);

  return (
    <div style={{ marginTop: 10 }}>
      {!props.disabled && (
        <div>
          <label className="btn btn-primary">
            <input
              id="my-file-selector"
              className="d-none"
              type="file"
              name={props.name}
              onChange={handleChange}
              accept={props.accept}
              multiple
              autoFocus={props.autoFocus ? true : false}
            />
            {props.titleButton}
          </label>
          {props.error && (
            <div
              className="error invalid-feedback"
              style={{ display: 'inline', width: '100%', marginLeft: '10px' }}
            >
              {props.error}
            </div>
          )}
        </div>
      )}
      {uploadProgress !== null && (
        <div style={{ width: tableWidth, marginBottom: 10, color: 'red' }}>
          <div className="progress">
            <div
              className="progress-bar"
              role="progressbar"
              style={{ width: `${uploadProgress}%` }}
              aria-valuenow={uploadProgress}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              {`${uploadProgress}%`}
            </div>
          </div>
        </div>
      )}
      {renderTable()}

    </div>
  );
};

MultiFileUploadS3.defaultProps = {
  onChange: () => { },
  onUpload: () => { },
  beforeUpload: () => { },
  path: '',
  accept: 'media_type',
  titleButton: 'Anexar',
  uploadFileLimit: 3,
  file: [],
  limitFileSize: 300,
  disabled: false,
};

export default MultiFileUploadS3;